import { useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";

interface OnOpenChangeOptions {
  replace?: boolean;
}
interface UseOpenStateOptions {
  id: string;
  externalState?: {
    isOpen: boolean;
    onOpenChange: (open: boolean, options?: OnOpenChangeOptions) => void;
  };
}

export function useOpenState({ id, externalState }: UseOpenStateOptions) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isClosing = useRef(false);

  // Use external state if provided, otherwise use internal URL-based state
  if (externalState) {
    return {
      id,
      isOpen: externalState.isOpen,
      handleOpenChange: externalState.onOpenChange,
    };
  }

  const paramKey = `${id}-state-open`;
  const isOpen = searchParams.get(paramKey) === "true";

  function handleOpenChange(open: boolean, options?: OnOpenChangeOptions) {
    const { replace = false } = options || {};

    if (open) {
      isClosing.current = false;
      const newSearchParams = replace ? new URLSearchParams() : new URLSearchParams(searchParams);
      newSearchParams.set(paramKey, "true");

      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace });
    } else if (!isClosing.current) {
      isClosing.current = true;
      navigate(-1);
    }
  }

  return {
    id,
    isOpen,
    handleOpenChange,
  };
}
